import { decimalesDe } from '@libreta/dominio';

/**
 * Formato de presentación. La base guarda el dato; acá se decide cómo se lee:
 * guaraníes con punto de miles y sin decimales, fechas en día/mes/año, instantes en la
 * zona de la ENTIDAD (no la del navegador de quien mira).
 */

const SIMBOLO: Record<string, string> = { PYG: 'Gs.', USD: 'US$', BRL: 'R$', ARS: '$' };

export function monto(valor: number, moneda = 'PYG', opciones: { simbolo?: boolean } = {}): string {
  const decimales = decimalesDe(moneda);
  const numero = (valor / 10 ** decimales).toLocaleString('es-PY', {
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales,
  });
  return opciones.simbolo === false ? numero : `${SIMBOLO[moneda] ?? moneda} ${numero}`;
}

/** Montos grandes en el tablero: 26,1 M. */
export function montoCorto(valor: number): string {
  const absoluto = Math.abs(valor);
  if (absoluto >= 1_000_000_000) return `${(valor / 1_000_000_000).toLocaleString('es-PY', { maximumFractionDigits: 1 })} mil M`;
  if (absoluto >= 1_000_000) return `${(valor / 1_000_000).toLocaleString('es-PY', { maximumFractionDigits: 1 })} M`;
  if (absoluto >= 10_000) return `${Math.round(valor / 1000).toLocaleString('es-PY')} mil`;
  return valor.toLocaleString('es-PY');
}

/** Lee lo que escribió la persona ("1.250.000", "1250000", "Gs. 1.250.000") como entero. */
export function leerMonto(texto: string, moneda = 'PYG'): number | null {
  const decimales = decimalesDe(moneda);
  const limpio = texto.replace(/[^\d,]/g, '');
  if (!limpio) return null;
  if (decimales === 0) return Number(limpio.replace(/,/g, ''));
  const [entero = '0', fraccion = ''] = limpio.split(',');
  return Number(entero) * 10 ** decimales + Number(fraccion.padEnd(decimales, '0').slice(0, decimales));
}

export function fecha(valor: string | null | undefined): string {
  if (!valor) return '—';
  const [anio, mes, dia] = valor.slice(0, 10).split('-');
  return `${dia}/${mes}/${anio}`;
}

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

export function fechaCorta(valor: string): string {
  const [, mes, dia] = valor.split('-');
  return `${Number(dia)} ${MESES[Number(mes) - 1]}`;
}

export function instante(valor: string | null | undefined, zona: string): string {
  if (!valor) return '—';
  return new Intl.DateTimeFormat('es-PY', {
    timeZone: zona, day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }).format(new Date(valor));
}

export function porcentaje(bp: number): string {
  return `${(bp / 100).toLocaleString('es-PY', { maximumFractionDigits: 2 })} %`;
}

export function dias(cantidad: number): string {
  return cantidad === 1 ? '1 día' : `${cantidad} días`;
}

export function iniciales(nombre: string): string {
  return nombre
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((parte) => parte[0]!.toUpperCase())
    .join('');
}
